/**
 * MoodMapSummary - Summary cards showing mood distribution and stats
 */

import { motion } from 'framer-motion';
import { useMoodMapStore, MOODS, CATEGORIES } from '../../stores/moodmapStore';
import { TrendingUp, Target, Heart, Zap } from 'lucide-react';

const MoodMapSummary = () => {
  const { moodDistribution, categoryStats, entries } = useMoodMapStore();

  // Calculate total entries this month
  const totalEntries = entries.length;

  // Calculate dominant mood
  const dominantMood = moodDistribution
    ? Object.entries(moodDistribution).reduce(
        (max, [mood, count]) => (count > max.count ? { mood, count } : max),
        { mood: null, count: 0 }
      )
    : null;

  // Calculate total items
  const totalItems = categoryStats
    ? Object.values(categoryStats).reduce((sum, count) => sum + count, 0)
    : 0;

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {/* Total Entries */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gray-900/50 border border-gray-800 rounded-xl p-4"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
            <Target className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <p className="text-2xl font-bold text-white">{totalEntries}</p>
            <p className="text-sm text-gray-400">Kayıt</p>
          </div>
        </div>
      </motion.div>

      {/* Dominant Mood */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-gray-900/50 border border-gray-800 rounded-xl p-4"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center text-xl">
            {dominantMood?.mood ? MOODS[dominantMood.mood]?.emoji : '😊'}
          </div>
          <div>
            <p className="text-lg font-bold text-white">
              {dominantMood?.mood ? MOODS[dominantMood.mood]?.label : '-'}
            </p>
            <p className="text-sm text-gray-400">Baskın Mod</p>
          </div>
        </div>
      </motion.div>

      {/* Wins Count */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-gray-900/50 border border-gray-800 rounded-xl p-4"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-green-400" />
          </div>
          <div>
            <p className="text-2xl font-bold text-white">
              {categoryStats?.win || 0}
            </p>
            <p className="text-sm text-gray-400">Başarı</p>
          </div>
        </div>
      </motion.div>

      {/* Total Items */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-gray-900/50 border border-gray-800 rounded-xl p-4"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
            <Zap className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <p className="text-2xl font-bold text-white">{totalItems}</p>
            <p className="text-sm text-gray-400">Toplam Not</p>
          </div>
        </div>
      </motion.div>

      {/* Mood Distribution Bar (spans full width on larger screens) */}
      {moodDistribution && totalEntries > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="col-span-2 lg:col-span-4 bg-gray-900/50 border border-gray-800 rounded-xl p-4"
        >
          <p className="text-sm text-gray-400 mb-3">Mood Dağılımı</p>
          <div className="flex items-center gap-1 h-6 rounded-lg overflow-hidden">
            {Object.entries(MOODS).map(([mood, config]) => {
              const count = moodDistribution[mood] || 0;
              const percentage = (count / totalEntries) * 100;
              if (percentage === 0) return null;

              const colorMap = {
                emerald: 'bg-emerald-500',
                green: 'bg-green-500',
                yellow: 'bg-yellow-500',
                orange: 'bg-orange-500',
                red: 'bg-red-500',
              };

              return (
                <div
                  key={mood}
                  className={`h-full ${colorMap[config.color]} transition-all`}
                  style={{ width: `${percentage}%` }}
                  title={`${config.label}: ${count} (${percentage.toFixed(0)}%)`}
                />
              );
            })}
          </div>
          <div className="flex items-center justify-center gap-4 mt-3">
            {Object.entries(MOODS).map(([mood, config]) => {
              const count = moodDistribution[mood] || 0;
              if (count === 0) return null;
              return (
                <div key={mood} className="flex items-center gap-1.5 text-sm">
                  <span>{config.emoji}</span>
                  <span className="text-gray-400">{count}</span>
                </div>
              );
            })}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default MoodMapSummary;
